'use client';

import clsx from 'clsx';
import { useMemo } from 'react';
import Select from 'react-select';
import { getErrorMessage } from '../utils/getErrorMessage';
import { SelectorStyles, selectorErrorMessage, selectorLabel, selectorWrapper } from './styles';
import type { SelectorOption, SelectorProps } from './types';
import { SelectorOptionTypes } from './types';
import { getOptionsComponent } from './utils/getOptionsComponent';
import ChevronDown from '/public/icons/chevron-down.svg';
import { getOptions } from './utils/getOptions';

export const Selector = <Value extends string | number>({
  label,
  options,
  isClearOption = true,
  clearOptionLabel,
  intent = 'default',
  optionType = SelectorOptionTypes.DEFAULT,
  value,
  option,
  onChange,
  customStyles,
  errors,
  name,
  disabled,
  required,
  className,
  helperText,
  ...rest
}: SelectorProps<Value>) => {
  const errorMessage = getErrorMessage(errors, name ?? '');

  const selectedOption = useMemo(() => {
    const found = options.find((opt) => opt.value === value);
    if (found) return found;
    return option ?? null;
  }, [options, option, value]);

  const optionsWithClearOption = useMemo(() => {
    if (isClearOption) {
      return getOptions(options, selectedOption, clearOptionLabel);
    }

    return options;
  }, [options, isClearOption, selectedOption, clearOptionLabel]);

  const isOldVersion = intent === 'old';

  return (
    <div className={clsx(selectorWrapper(), className)}>
      {label && <p className={selectorLabel({ intent, required })}>{label}</p>}
      <Select<SelectorOption<Value>>
        options={optionsWithClearOption}
        value={selectedOption}
        required={required}
        onChange={onChange}
        components={{
          DropdownIndicator: (props) => (
            <ChevronDown
              className={`transition-transform duration-200 text-black fill-current w-4 h-4 ${props.selectProps.menuIsOpen ? 'rotate-180' : ''}`}
            />
          ),
          Option: (props) => getOptionsComponent(optionType)({ ...props, intent }),
          NoOptionsMessage: () => <p className="text-body-responsive text-grey-700 text-center py-1">No options</p>,
          LoadingMessage: () => <p className="text-body-responsive text-grey-700 text-center py-1">Loading...</p>,
        }}
        styles={SelectorStyles({
          invalid: Boolean(errorMessage),
          customStyles,
          isOldVersion,
        })}
        {...rest}
        isDisabled={disabled}
      />
      {errorMessage && <p className={selectorErrorMessage()}>{errorMessage}</p>}
      {helperText && <p className="text-blue-300 text-body-small">{helperText}</p>}
    </div>
  );
};
